import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store, select } from '@ngrx/store';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { Configuration } from '@shared/models/configuration';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { getUserInfo } from '@shared/store/selectors/security.selector';
import { UserInfo } from '@shared/models/user-info.model';
import { TabIdService } from '@core/services/tab-id.service';
interface PresencePublishPayload {
  type: 'heartbeat' | 'disconnect';
  tabId: string;
  url: string;
  title: string;
  visible: boolean;
  timestamp: string;
}

interface TickIdentity {
  companyId: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface TickRequest {
  identity: TickIdentity;
  data: PresencePublishPayload;
}

/**
 * PresenceTrackingService — Path B (REST upstream).
 *
 * SAFETY CONTRACT: This service MUST NOT disrupt the host application.
 *   - Every public method is wrapped in try/catch so no throw escapes.
 *   - Every HTTP POST is fire-and-forget with a logged (not thrown) error.
 *   - A circuit breaker stops posting after repeated failures so a broken
 *     presence-tracker cannot generate endless network/console noise.
 *   - A kill switch (Configuration.getConfig('DisablePresenceTracking') === true)
 *     short-circuits the whole feature.
 *   - If anything goes wrong during construction or bootstrap, the feature
 *     silently disables itself instead of propagating errors upward.
 */
@Injectable({ providedIn: 'root' })
export class PresenceTrackingService implements OnDestroy {
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly securityStore = inject<Store<SecurityState>>(Store);

  // The tab's id, shared with the call (TabIdService): the same across a
  // refresh, so HSET overwrites the same Redis field → no duplicate; new per
  // tab; and gone with the tab, when the beacon disconnect drops the entry.
  // Read live: a duplicated tab takes a fresh id just after load (ITP-E-175-04).
  private readonly tabIds = inject(TabIdService);
  private get tabId(): string {
    return this.tabIds.id;
  }

  private destroy$ = new Subject<void>();
  private identity: TickIdentity | null = null;
  private started = false;
  private disabled = false;
  /** Idempotency guard: beforeunload and ngOnDestroy can both fire for a
   *  single tab close. Without this we'd send two disconnects. */
  private disconnectSent = false;
  private visibilityHandler: (() => void) | null = null;
  private beforeUnloadHandler: (() => void) | null = null;

  // Circuit breaker: after this many consecutive POST failures, stop sending
  // ticks for a cool-down window. Prevents flooding logs / network.
  private static readonly MAX_CONSECUTIVE_FAILURES = 3;
  private static readonly COOL_DOWN_MS = 5 * 60 * 1000; // 5 minutes
  private consecutiveFailures = 0;
  private cooldownUntil = 0;

  // Rate limit: suppress heartbeats more frequent than this interval.
  // Prevents rapid-fire POSTs when a user alt-tabs quickly between windows.
  private static readonly MIN_HEARTBEAT_INTERVAL_MS = 5000;
  private lastHeartbeatAt = 0;

  /**
   * Idempotent. Call once after auth is ready.
   * Never throws. If anything goes wrong, the feature disables itself silently.
   */
  startTracking(): void {
    try {
      if (this.started || this.disabled) return;
      if (this.killSwitchEnabled()) {
        this.disabled = true;
        return;
      }
      this.securityStore.pipe(
        select(getUserInfo),
        filter((u: UserInfo): u is UserInfo => !!u && !!u.companyId && !!u.email),
        take(1),
        takeUntil(this.destroy$)
      ).subscribe({
        next: userInfo => {
          try { this.bootstrap(userInfo); } catch (err) {
            console.warn('[presence] bootstrap failed, disabling', err);
            this.disabled = true;
          }
        },
        error: err => {
          console.warn('[presence] userInfo selector errored, disabling', err);
          this.disabled = true;
        },
      });
    } catch (err) {
      console.warn('[presence] startTracking guard threw, disabling', err);
      this.disabled = true;
    }
  }

  stopTracking(): void {
    try {
      if (!this.started) return;
      try { this.publishDisconnect(); } catch { /* swallow */ }
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        this.visibilityHandler = null;
      }
      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        this.beforeUnloadHandler = null;
      }
      this.destroy$.next();
      this.destroy$.complete();
      this.destroy$ = new Subject<void>(); // re-create for potential re-entry (logout → re-login)
      this.started = false;
      this.disconnectSent = false;
    } catch (err) {
      console.warn('[presence] stopTracking threw', err);
    }
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }

  private bootstrap(userInfo: UserInfo): void {
    this.identity = {
      companyId: userInfo.companyId,
      email: userInfo.email,
      firstName: userInfo.firstName ?? '',
      lastName: userInfo.lastName ?? '',
    };

    this.publishHeartbeat();

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe({
      next: e => { try { this.publishHeartbeat(e.urlAfterRedirects); } catch { /* swallow */ } },
      error: err => console.warn('[presence] router events errored', err),
    });

    this.visibilityHandler = () => { try { this.publishHeartbeat(); } catch { /* swallow */ } };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.beforeUnloadHandler = () => { try { this.publishDisconnect(); } catch { /* swallow */ } };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    this.started = true;
  }

  private publishHeartbeat(rawUrl?: string): void {
    if (!this.identity || this.disabled) return;
    // Rate-limit: skip if last heartbeat was less than MIN_HEARTBEAT_INTERVAL_MS ago.
    // Prevents rapid-fire POSTs when user alt-tabs quickly.
    const now = Date.now();
    if (now - this.lastHeartbeatAt < PresenceTrackingService.MIN_HEARTBEAT_INTERVAL_MS) return;
    this.lastHeartbeatAt = now;
    const payload: PresencePublishPayload = {
      type: 'heartbeat',
      tabId: this.tabId,
      url: this.sanitizeUrl(rawUrl ?? window.location.pathname),
      title: (document.title ?? '').slice(0, 200),
      visible: document.visibilityState === 'visible',
      timestamp: new Date().toISOString(),
    };
    this.postTick(payload);
  }

  private publishDisconnect(): void {
    if (!this.identity || this.disabled) return;
    if (this.disconnectSent) return; // idempotency — beforeunload + ngOnDestroy can both fire
    this.disconnectSent = true;
    const payload: PresencePublishPayload = {
      type: 'disconnect',
      tabId: this.tabId,
      url: this.sanitizeUrl(window.location.pathname),
      title: document.title,
      visible: false,
      timestamp: new Date().toISOString(),
    };
    // Use sendBeacon — it survives page unload, which XHR / fetch normally don't.
    // Falls back to regular POST if the browser doesn't support sendBeacon.
    if (!this.sendBeaconTick(payload)) {
      this.postTick(payload);
    }
  }

  /**
   * Fire a beacon-delivered tick. Returns true if the beacon was queued.
   * Beacons survive page unload where XHRs / fetch requests get canceled.
   *
   * We send body as a Blob with type 'text/plain' to avoid triggering a CORS
   * preflight — 'application/json' would require an OPTIONS round-trip which
   * the browser may not complete before unload. Our Gin handler uses
   * json.Decode on the raw body, so the Content-Type header is not enforced.
   */
  private sendBeaconTick(data: PresencePublishPayload): boolean {
    if (!this.identity || this.disabled) return false;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return false;
    }
    let url: string;
    try {
      const base = this.resolveBaseUrl();
      if (!base) return false;
      url = `${base}/v1/presence/tick`;
    } catch { return false; }
    const body: TickRequest = { identity: this.identity, data };
    try {
      const blob = new Blob([JSON.stringify(body)], { type: 'text/plain;charset=UTF-8' });
      return navigator.sendBeacon(url, blob);
    } catch (err) {
      console.warn('[presence] sendBeacon failed, falling back to XHR', err);
      return false;
    }
  }

  /**
   * Fire-and-forget POST to presence-tracker.
   * Failures increment the circuit breaker; errors are logged, never thrown.
   */
  private postTick(data: PresencePublishPayload): void {
    if (!this.identity || this.disabled) return;

    // Circuit breaker cool-down — quietly skip while the window is active.
    const now = Date.now();
    if (this.cooldownUntil > now) return;

    let url: string;
    try {
      const base = this.resolveBaseUrl();
      if (!base) return; // mis-configured — silently skip
      url = `${base}/v1/presence/tick`;
    } catch (err) {
      console.warn('[presence] bad PresenceTrackerApi config, disabling', err);
      this.disabled = true;
      return;
    }

    const body: TickRequest = { identity: this.identity, data };
    try {
      this.httpClient.post(url, body).subscribe({
        next: () => { this.consecutiveFailures = 0; },
        error: (err) => this.onTickFailure(err),
      });
    } catch (err) {
      this.onTickFailure(err);
    }
  }

  private onTickFailure(err: unknown): void {
    this.consecutiveFailures++;
    if (this.consecutiveFailures >= PresenceTrackingService.MAX_CONSECUTIVE_FAILURES) {
      this.cooldownUntil = Date.now() + PresenceTrackingService.COOL_DOWN_MS;
      console.warn(
        `[presence] tick failed ${this.consecutiveFailures}x in a row, ` +
        `pausing for ${PresenceTrackingService.COOL_DOWN_MS / 60000}min`, err,
      );
      this.consecutiveFailures = 0;
    } else {
      console.warn('[presence] tick failed', err);
    }
  }

  private resolveBaseUrl(): string | null {
    const raw = Configuration.getConfig('PresenceTrackerApi');
    const str = (raw ?? '').toString().trim();
    if (!str) return null; // not configured → silently skip all heartbeats
    return str.replace(/\/$/, '');
  }

  private killSwitchEnabled(): boolean {
    try {
      return Configuration.getConfig('DisablePresenceTracking') === true;
    } catch {
      return false;
    }
  }

  /**
   * Strip query strings, fragments, and limit to 500 chars.
   * Query params may carry tokens; never publish them.
   */
  private sanitizeUrl(rawUrl: string): string {
    if (!rawUrl) return '/';
    const noQuery = rawUrl.split('?')[0].split('#')[0];
    return noQuery.length > 500 ? noQuery.slice(0, 500) : noQuery;
  }
}
